import React, { useCallback, useMemo } from "react";
import { DataTable, FilterPanel, IconButton, Button } from "../";
import { FaUserPlus } from "react-icons/fa";
import { TbTransactionDollar } from "react-icons/tb";

// Column definitions
const supportColumns = [
  {
    key: "link",
    dataKey: "childId",
    title: "Link",
    width: 70,
    sortable: false,
    filterable: false,
    render: (rowData, onSelectChild) => (
      <IconButton
        icon={<FaUserPlus />}
        size="sm"
        data-testid={`child-link-${rowData.childId}`}
        appearance="primary"
        className="children-table-action-button"
        onClick={() => onSelectChild && onSelectChild(rowData)}
        title="Match to Child"
        height={30}
        width={30}
      />
    ),
  },
  {
    key: "selectTransaction",
    dataKey: "childId",
    title: "Select",
    width: 70,
    sortable: false,
    filterable: false,
    render: (rowData, onSelectChild) => (
      <IconButton
        icon={<TbTransactionDollar />}
        size="sm"
        data-testid={`child-transactions-${rowData.childId}`}
        appearance="primary"
        className="children-table-action-button"
        onClick={() => onSelectChild && onSelectChild(rowData)}
        title="View transactions for this child"
        height={30}
        width={30}
      />
    ),
  },
    {
      key: "childId",
      dataKey: "childId",
      title: "Child ID",
      width: 150,
      sortable: true,
      filterable: true,
    },
    {
      key: "firstName",
      dataKey: "firstName",
      title: "First Name",
      flexGrow: 1,
      sortable: true,
      filterable: true,
    },
    {
      key: "surname",
      dataKey: "surname",
      title: "Surname",
      flexGrow: 1,
      sortable: true,
      filterable: true,
    },
    {
      key: "familyName",
      dataKey: "familyName",
      title: "Family Name",
      flexGrow: 2,
      sortable: true,
      filterable: true,
    },
    {
        key: "centreName",
        dataKey: "centreName",
        title: "Centre Name",
        flexGrow: 2,
        sortable: true,
        filterable: true,
    },

    {
      key: "birthDate",
      dataKey: "birthDate",
      title: "Birth Date",
      width: 130,
      sortable: true,
      filterable: true,
      render: (rowData) => {
        if (!rowData.birthDate) return "";
        try {
          const date = new Date(rowData.birthDate);
          return date.toLocaleDateString("en-NZ", { day: "2-digit", month: "2-digit", year: "numeric" });
        } catch {
          return rowData.birthDate;
        }
      }
    },
      {
        key: "siblingName",
        dataKey: "siblingName",
        title: "Sibling Name",
        flexGrow: 2,
        sortable: true,
        filterable: true,
      },
      {
        key: "siblingChildId",
        dataKey: "siblingChildId",
        title: "Sibling Child ID",
        width: 150,
        sortable: true,
        filterable: true,
      },
      {
        key: "statusDescription",
        dataKey: "statusDescription",
        title: "Status",
        width: 170,
        sortable: true,
        filterable: true,
      }
  ];

export default function ChildrenTable({ 
  data,
  onSelectChild,
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  isCheckPickerOpen,
  activeFilterColumnKey,
  filterOptions,
  selectedFilterValues,
  onApplyFilters,
  onCancelFiltering,
  onFilterChange,
  selectTransactions = false,
  showLink = true,
}) {
  // Build columns with conditional display based on selectTransactions/showLink
  const columns = useMemo(() => {
    // Start with all support columns
    let cols = [...supportColumns];
    
    // Conditionally include link and selectTransaction columns
    if (!showLink) {
      cols = cols.filter(col => col.key !== 'link');
    }
    if (!selectTransactions) {
      cols = cols.filter(col => col.key !== 'selectTransaction');
    }
    
    // Map columns with proper render functions
    return cols.map(col => ({
      ...col,
      render: col.render ? (rowData, rowIndex) => {
        // Pass onSelectChild to action columns
        if (col.key === 'link' || col.key === 'selectTransaction') {
          return col.render(rowData, onSelectChild);
        }
        return col.render(rowData, rowIndex);
      } : undefined
    }));
  }, [selectTransactions, showLink, onSelectChild]);

  return (
    <div className="children-table-container">
      {isCheckPickerOpen && activeFilterColumnKey && (
        <FilterPanel
          activeColumn={activeFilterColumnKey}
          filterOptions={filterOptions}
          selectedValues={selectedFilterValues}
          onApply={onApplyFilters}
          onCancel={onCancelFiltering}
          onFilterChange={onFilterChange}
        />
      )}
      <DataTable
        data={data}
        columns={columns}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        loading={loading}
        activeFilters={activeFilters}
        onFilterClick={onFilterClick}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        rowKey="childId"
        showRowCount={true}
        fillHeight={false}
        autoHeight={true}
        height={400}
        rowHeight={10}
        limitOptions={[5, 10, 20]}
      />
    </div>
  );
}
